/**
 * PCB Service - Handles PCB Gerber file operations
 *
 * Gerber files are stored in Google Drive with the following structure:
 * [PCB_GERBER_FOLDER_ID]/
 *   PCB-00001/
 *     *.gtl (top copper), *.gbl (bottom copper)
 *     *.gts (top solder mask), *.gbs (bottom solder mask)
 *     *.gto (top silkscreen), *.gbo (bottom silkscreen)
 *     *.drl (drill), *.gko (outline)
 */

import { logger } from '@/utils/logger';

const API_BASE = '/api/v1/pcb';

// Helper function for authenticated requests
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('auth_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response;
}

/**
 * Gerber file with base64 encoded content
 */
export interface GerberFile {
  name: string;
  content: string;  // Base64 encoded
  mime_type: string;
}

/**
 * Response from GetGerberFiles endpoint
 */
export interface GerberFilesResponse {
  part_number: string;
  folder_id: string;
  files: GerberFile[];
}

/**
 * Response from ListGerberFileNames endpoint
 */
export interface GerberFileNamesResponse {
  part_number: string;
  folder_id: string;
  files: string[];
}

/**
 * Check if a part number is a PCB part (starts with "PCB-")
 */
export function isPCBPart(partNumber: string | undefined | null): boolean {
  return !!partNumber && partNumber.startsWith('PCB-');
}

/**
 * Get all Gerber files for a PCB part with content
 * @param partNumber - PCB part number (e.g., "PCB-00001")
 */
export async function getGerberFiles(partNumber: string): Promise<GerberFilesResponse> {
  const response = await fetchWithAuth(`${API_BASE}/${encodeURIComponent(partNumber)}/gerber`);
  return response.json();
}

/**
 * List Gerber file names only (lighter endpoint)
 * @param partNumber - PCB part number (e.g., "PCB-00001")
 */
export async function listGerberFileNames(partNumber: string): Promise<GerberFileNamesResponse> {
  const response = await fetchWithAuth(`${API_BASE}/${encodeURIComponent(partNumber)}/gerber/list`);
  return response.json();
}

/**
 * Decode base64 content to string
 */
export function decodeGerberContent(base64Content: string): string {
  return atob(base64Content);
}

/**
 * Get file extension from filename
 */
export function getFileExtension(filename: string): string {
  const parts = filename.toLowerCase().split('.');
  return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
}

/**
 * Gerber layer type identification
 */
export type GerberLayerType =
  | 'top-copper'
  | 'bottom-copper'
  | 'top-soldermask'
  | 'bottom-soldermask'
  | 'top-silkscreen'
  | 'bottom-silkscreen'
  | 'top-paste'
  | 'bottom-paste'
  | 'outline'
  | 'drill'
  | 'mechanical'
  | 'unknown';

/**
 * Identify Gerber layer type from filename extension
 */
export function identifyLayerType(filename: string): GerberLayerType {
  const ext = getFileExtension(filename);

  const layerMap: Record<string, GerberLayerType> = {
    '.gtl': 'top-copper',
    '.gbl': 'bottom-copper',
    '.gts': 'top-soldermask',
    '.gbs': 'bottom-soldermask',
    '.gto': 'top-silkscreen',
    '.gbo': 'bottom-silkscreen',
    '.gtp': 'top-paste',
    '.gbp': 'bottom-paste',
    '.gko': 'outline',
    '.drl': 'drill',
    '.xln': 'drill',
    '.gm1': 'mechanical',
    '.gm2': 'mechanical',
    '.gm3': 'mechanical',
  };

  return layerMap[ext] || 'unknown';
}

/**
 * Check if a file is a top-side layer
 */
export function isTopSideLayer(filename: string): boolean {
  const layerType = identifyLayerType(filename);
  return layerType.startsWith('top-') || layerType === 'outline' || layerType === 'drill';
}

/**
 * Check if a file is a bottom-side layer
 */
export function isBottomSideLayer(filename: string): boolean {
  const layerType = identifyLayerType(filename);
  return layerType.startsWith('bottom-') || layerType === 'outline' || layerType === 'drill';
}

/**
 * Check if a PCB part has Gerber files available
 * Returns true if files exist, false otherwise
 * @param partNumber - PCB part number (e.g., "PCB-00001")
 */
export async function hasGerberFiles(partNumber: string): Promise<boolean> {
  try {
    const response = await listGerberFileNames(partNumber);
    return response.files && response.files.length > 0;
  } catch {
    // If API call fails (e.g., folder not found), return false
    return false;
  }
}

/**
 * Check Gerber file availability for multiple PCB parts
 * Returns a map of part number -> has files
 * @param partNumbers - Array of PCB part numbers
 */
export async function checkGerberAvailability(partNumbers: string[]): Promise<Record<string, boolean>> {
  const results: Record<string, boolean> = {};

  // Check all parts in parallel
  await Promise.all(
    partNumbers.map(async (partNumber) => {
      results[partNumber] = await hasGerberFiles(partNumber);
    })
  );

  return results;
}

// ============================================================================
// Folder Management APIs
// ============================================================================

/**
 * Gerber folder information
 */
export interface GerberFolderInfo {
  id: string;
  name: string;
  file_count: number;
}

/**
 * Response from ListGerberFolders endpoint
 */
export interface GerberFoldersResponse {
  root_folder_id: string;
  folders: GerberFolderInfo[];
}

/**
 * Response from uploading Gerber files
 */
export interface UploadGerberResponse {
  part_number: string;
  folder_id: string;
  uploaded_files: string[];
  errors?: string[];
}

/**
 * List all Gerber folders in Google Drive
 */
export async function listGerberFolders(): Promise<GerberFoldersResponse> {
  const response = await fetchWithAuth(`${API_BASE}/folders`);
  return response.json();
}

/**
 * Create a new Gerber folder for a PCB part
 * @param partNumber - PCB part number (e.g., "PCB-00001")
 */
export async function createGerberFolder(partNumber: string): Promise<GerberFolderInfo> {
  const response = await fetchWithAuth(`${API_BASE}/folders`, {
    method: 'POST',
    body: JSON.stringify({ part_number: partNumber }),
  });
  return response.json();
}

/**
 * Upload Gerber files for a PCB part
 * @param partNumber - PCB part number (e.g., "PCB-00001")
 * @param files - Array of files to upload
 */
export async function uploadGerberFiles(partNumber: string, files: File[]): Promise<UploadGerberResponse> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file);
  });

  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/${encodeURIComponent(partNumber)}/gerber/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Upload progress event from SSE
 */
export interface UploadProgress {
  type: 'start' | 'progress' | 'complete' | 'error';
  message: string;
  file_name?: string;
  current: number;
  total: number;
  percentage: number;
}

/**
 * Upload an archive file (zip/rar) for a PCB part with SSE progress streaming
 * The backend will extract the archive and upload the Gerber files to Google Drive
 * @param partNumber - PCB part number (e.g., "PCB-00001")
 * @param file - Archive file to upload (.zip or .rar)
 * @param onProgress - Optional callback for progress updates
 */
export async function uploadArchiveFile(
  partNumber: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadGerberResponse> {
  const token = localStorage.getItem('auth_token');
  const formData = new FormData();
  formData.append('archive', file);

  const headers: HeadersInit = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/${encodeURIComponent(partNumber)}/gerber/upload-archive`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  // Check if response is SSE (text/event-stream)
  const contentType = response.headers.get('Content-Type');
  if (contentType?.includes('text/event-stream')) {
    // Handle SSE stream
    return handleSSEUploadResponse(response, onProgress);
  }

  // Fallback to JSON response (for backwards compatibility)
  return response.json();
}

/**
 * Handle SSE response for archive upload
 */
async function handleSSEUploadResponse(
  response: Response,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadGerberResponse> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Response body is not readable');
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let result: UploadGerberResponse | null = null;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE messages (separated by double newline)
      const messages = buffer.split('\n\n');
      buffer = messages.pop() || ''; // Keep incomplete message in buffer

      for (const message of messages) {
        if (!message.trim()) continue;

        const lines = message.split('\n');
        let eventType = '';
        let eventData = '';

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            eventData = line.slice(6);
          }
        }

        if (!eventData) continue;

        try {
          const parsed = JSON.parse(eventData);

          // Check if this is the final complete event
          if (eventType === 'complete' || (parsed.part_number && parsed.folder_id && parsed.uploaded_files)) {
            result = parsed as UploadGerberResponse;
          } else if (onProgress && parsed.type) {
            // Progress update
            onProgress(parsed as UploadProgress);
          }
        } catch {
          // Ignore parse errors for incomplete JSON
          logger.debug('SSE parse error for:', eventData);
        }
      }
    }

    // Process any remaining buffer
    if (buffer.trim()) {
      const lines = buffer.split('\n');
      let eventData = '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          eventData = line.slice(6);
        }
      }

      if (eventData) {
        try {
          const parsed = JSON.parse(eventData);
          if (parsed.part_number && parsed.folder_id && parsed.uploaded_files) {
            result = parsed as UploadGerberResponse;
          }
        } catch {
          // Ignore
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  if (!result) {
    throw new Error('Upload completed but no response received');
  }

  return result;
}

/**
 * Open Google Drive folder in a new tab
 * @param folderId - Google Drive folder ID
 */
export function openGoogleDriveFolder(folderId: string): void {
  window.open(`https://drive.google.com/drive/folders/${folderId}`, '_blank');
}

// ============================================================================
// Drive Folder Picker APIs (for Add Gerber Dialog)
// ============================================================================

/**
 * Drive folder info with additional metadata
 */
export interface DriveFolderInfo {
  id: string;
  name: string;
  file_count: number;
  created_at?: string;
}

/**
 * Response from ListDriveFolders endpoint
 */
export interface ListDriveFoldersResponse {
  root_folder_id: string;
  folders: DriveFolderInfo[];
}

/**
 * Response from LinkDriveFolder endpoint
 */
export interface LinkDriveFolderResponse {
  part_number: string;
  folder_id: string;
  old_name: string;
  new_name: string;
  file_count: number;
}

/**
 * List all folders in the PCB Gerber Google Drive folder for picking
 */
export async function listDriveFolders(): Promise<ListDriveFoldersResponse> {
  const response = await fetchWithAuth(`${API_BASE}/drive/folders`);
  return response.json();
}

/**
 * Link an existing Google Drive folder to a PCB part
 * This renames the folder with the part number prefix (e.g., "PCB-00001 - CAM for HR_MIV6")
 * @param partNumber - PCB part number (e.g., "PCB-00001")
 * @param folderId - Google Drive folder ID to link
 */
export async function linkDriveFolder(partNumber: string, folderId: string): Promise<LinkDriveFolderResponse> {
  const response = await fetchWithAuth(`${API_BASE}/${encodeURIComponent(partNumber)}/drive/link`, {
    method: 'POST',
    body: JSON.stringify({ folder_id: folderId }),
  });
  return response.json();
}
